import { ProductListItem, ProductPricing, ProductDetails } from '../types/global';

/**
 * Get pricing for a specific currency and billing period
 */
export const getPricingOption = (
  product: ProductDetails,
  currency: string,
  billingPeriod: 'ONE_TIME' | 'MONTHLY' | 'YEARLY'
): ProductPricing | undefined => {
  return product.pricing.find(
    (p) => p.currency === currency && p.billing_period === billingPeriod && p.is_active
  );
};

/**
 * Get all available currencies for a product
 */
export const getAvailableCurrencies = (product: ProductDetails): string[] => {
  const currencies = new Set(
    product.pricing.filter((p) => p.is_active).map((p) => p.currency)
  );
  return Array.from(currencies);
};

/**
 * Get all available billing periods for a product
 */
export const getAvailableBillingPeriods = (
  product: ProductDetails
): ('ONE_TIME' | 'MONTHLY' | 'YEARLY')[] => {
  const periods = new Set(
    product.pricing.filter((p) => p.is_active).map((p) => p.billing_period)
  );
  return Array.from(periods);
};

/**
 * Filter products by user status
 */
export const filterProductsByUserStatus = (
  products: ProductListItem[],
  userStatus: 'NEW' | 'EXISTING' | string
): ProductListItem[] => {
  return products.filter(
    (item) => item.is_active && (item.user_status === userStatus || item.user_status === null)
  );
};

/**
 * Filter products by app name
 */
export const filterProductsByApp = (
  products: ProductListItem[],
  appName: string
): ProductListItem[] => {
  return products.filter((item) => item.is_active && item.app_name === appName);
};

/**
 * Get product by ID
 */
export const getProductById = (
  products: ProductListItem[],
  productId: number
): ProductListItem | undefined => {
  return products.find((item) => item.product_id === productId);
};

/**
 * Get the cheapest price for a product in a given currency
 */
export const getCheapestPrice = (
  product: ProductDetails,
  currency: string
): ProductPricing | undefined => {
  const pricesInCurrency = product.pricing.filter(
    (p) => p.currency === currency && p.is_active
  );

  if (pricesInCurrency.length === 0) return undefined;

  return pricesInCurrency.reduce((cheapest, current) =>
    current.unit_price < cheapest.unit_price ? current : cheapest
  );
};

/**
 * Sort products by tier (ascending or descending)
 */
export const sortProductsByTier = (
  products: ProductListItem[],
  order: 'asc' | 'desc' = 'asc'
): ProductListItem[] => {
  return [...products].sort((a, b) => {
    const diff = a.product.tier - b.product.tier;
    return order === 'asc' ? diff : -diff;
  });
};

/**
 * Check if a product has a specific billing period available
 */
export const hasBillingPeriod = (
  product: ProductDetails,
  billingPeriod: 'ONE_TIME' | 'MONTHLY' | 'YEARLY'
): boolean => {
  return product.pricing.some(
    (p) => p.billing_period === billingPeriod && p.is_active
  );
};

/**
 * Get formatted price string
 */
export const formatPrice = (price: number, currency: string): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(price);
};

/**
 * Get billing period display text
 */
export const getBillingPeriodText = (
  billingPeriod: 'ONE_TIME' | 'MONTHLY' | 'YEARLY'
): string => {
  const textMap = {
    ONE_TIME: 'One-time',
    MONTHLY: 'per month',
    YEARLY: 'per year',
  };
  return textMap[billingPeriod];
};
